import axios from "axios"
import apiEnpoint from "./config"
import localStorageService from "./localStorage.service"
import store from "../store/store"
import { getSalonSlug } from "../utils/salonHost"

const http = axios.create({
  baseURL: apiEnpoint,
})

const normalizeApiPath = (url) => {
  if (!url) return ""
  return String(url)
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\//, "")
}

const isScopedApiUrl = (url) => {
  const normalized = normalizeApiPath(url)
  if (!normalized.startsWith("api/")) return false
  const path = normalized.slice(4)
  if (path.startsWith("companies/")) return true
  const scopedPrefixes = [
    "users",
    "clients",
    "services",
    "records",
    "comments",
    "messaging",
  ]
  return scopedPrefixes.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  )
}

const shouldAttachCompanyIdHeader = (pathname) =>
  pathname.startsWith("/admin") || pathname.startsWith("/crm")

const resolveHostCompanyId = (salonSlug) => {
  if (!salonSlug) return null
  const hostCompany = store.getState().company
  if (hostCompany?.slug === salonSlug && hostCompany?.id != null) {
    return hostCompany.id
  }
  return null
}

const resolveScopedCompanyIdHeader = (salonSlug, pathname) => {
  const hostCompanyId = resolveHostCompanyId(salonSlug)
  if (hostCompanyId != null) {
    return hostCompanyId
  }

  if (pathname.startsWith("/client")) {
    return null
  }

  const profile = localStorageService.getUserProfile()
  return localStorageService.resolveScopedCompanyId(profile)
}

const resolveAuthToken = (pathname, userToken, clientToken) => {
  const isAdminRoute = pathname.startsWith("/admin")
  const isClientRoute = pathname.startsWith("/client")

  if (isAdminRoute && userToken) return userToken
  if (isClientRoute) return clientToken || null
  return userToken || clientToken
}

http.interceptors.request.use((config) => {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : ""
  const salonSlug = getSalonSlug()
  const userToken = localStorageService.getUserAccessToken()
  const clientToken = localStorageService.getClientAccessToken()
  const token = resolveAuthToken(pathname, userToken, clientToken)
  if (token) {
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
  }

  if (salonSlug) {
    config.headers["X-Company-Slug"] = salonSlug
  }

  const scopedUrl = isScopedApiUrl(config.url)
  if (scopedUrl && shouldAttachCompanyIdHeader(pathname)) {
    const companyId = resolveScopedCompanyIdHeader(salonSlug, pathname)
    if (companyId != null) {
      config.headers["X-Company-Id"] = String(companyId)
    }
  }

  return config
})

const httpService = {
  get: http.get.bind(http),
  post: http.post.bind(http),
  put: http.put.bind(http),
  delete: http.delete.bind(http),
  patch: http.patch.bind(http),
  instance: http,
}

export default httpService
